import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MapPin, Briefcase, Users } from "lucide-react";

interface JobBoardCardJob {
  id: number;
  title: string;
  status: string;
  location?: string;
  seniority?: string;
  customer_name?: string;
  customer_company_name?: string;
  application_count?: number;
  owner_name?: string;
  responsible_sales_name?: string;
  responsible_recruiter_name?: string;
  skills?: string | string[];
}

interface JobBoardCardProps {
  job: JobBoardCardJob;
  onNavigate: (jobId: number) => void;
  isDragOverlay?: boolean;
}

export const JobBoardCard: React.FC<JobBoardCardProps> = ({
  job,
  onNavigate,
  isDragOverlay = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(job.id), disabled: isDragOverlay });

  const style = isDragOverlay
    ? undefined
    : {
        transform: CSS.Transform.toString(transform),
        transition,
      };

  const parseSkills = (): string[] => {
    if (Array.isArray(job.skills)) return job.skills;
    if (typeof job.skills === "string") {
      return job.skills
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  };
  const skills = parseSkills();

  const customerDisplay = job.customer_name || job.customer_company_name;
  const appCount = job.application_count ?? 0;

  return (
    <div
      ref={isDragOverlay ? undefined : setNodeRef}
      style={style}
      className={`status-kanban-card ${isDragging ? "status-kanban-card--dragging" : ""} ${isDragOverlay ? "status-kanban-card--overlay" : ""}`}
      data-testid={`board-card-job-${job.id}`}
      {...(isDragOverlay ? {} : { ...attributes, ...listeners })}
    >
      <button
        type="button"
        className="status-kanban-card__title"
        onClick={(e) => {
          e.stopPropagation();
          onNavigate(job.id);
        }}
        style={{ cursor: "pointer" }}
      >
        {job.title}
      </button>

      <div className="status-kanban-card__meta">
        {job.location && (
          <span className="status-kanban-card__meta-item">
            <MapPin size={11} />
            {job.location}
          </span>
        )}
        {job.seniority && (
          <span className="status-kanban-card__meta-item">
            <Briefcase size={11} />
            {job.seniority}
          </span>
        )}
        {appCount > 0 && (
          <span className="status-kanban-card__meta-item">
            <Users size={11} />
            {appCount} app{appCount === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {customerDisplay && (
        <div className="status-kanban-card__meta">
          <span
            className="status-kanban-card__meta-item"
            style={{ color: "#4b5563" }}
          >
            {customerDisplay}
          </span>
        </div>
      )}

      {skills.length > 0 && (
        <div className="status-kanban-card__skills">
          {skills.slice(0, 3).map((skill) => (
            <span key={skill} className="status-kanban-card__skill-tag">
              {skill}
            </span>
          ))}
          {skills.length > 3 && (
            <span
              className="status-kanban-card__skill-tag"
              style={{ background: "#f3f4f6", color: "#6b7280" }}
            >
              +{skills.length - 3}
            </span>
          )}
        </div>
      )}

      {(job.owner_name ||
        job.responsible_sales_name ||
        job.responsible_recruiter_name) && (
        <div className="status-kanban-card__footer">
          {job.owner_name && <span>{job.owner_name}</span>}
          <div className="status-kanban-card__personnel">
            {job.responsible_sales_name && (
              <span className="status-kanban-card__personnel-item">
                KAM: {job.responsible_sales_name}
              </span>
            )}
            {job.responsible_recruiter_name && (
              <span className="status-kanban-card__personnel-item">
                Recruiter: {job.responsible_recruiter_name}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
